import API from '../config/api';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { createPortal } from 'react-dom';

import axios from 'axios';

import { Link } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

import { readStoredUser } from '../utils/productTracking';

const WELCOME = {
  role: 'bot',
  text: "Hi! I'm ShopGenie 🧞 Looking for idols, malas, incense or puja essentials? Tell me what you need and I'll compare options or suggest the best picks from our store.",
  products: []
};

const SUGGESTIONS = [
  'Suggest a brass idol for my home temple',
  'Compare the incense you have',
  'Best puja items for daily worship?',
  'Which idol is good for gifting?'
];

/* ------------------------------------------------- saved conversations -- */

/*
  Past chats live in localStorage rather than the database. A conversation is
  a browsing aid, not a record the store needs to keep — this way there is no
  schema, no endpoint and nothing to clean up, and a signed-out visitor keeps
  their history too. The cost is that it stays on this one browser.

  The key is per-user so two accounts sharing a computer never see each
  other's chats.
*/
const HISTORY_LIMIT = 20;

const historyKey = () => {
  const user = readStoredUser();

  return `shopgenie-chats:${(user && (user._id || user.email)) || 'guest'}`;
};

const loadChats = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(historyKey()) || '[]');

    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    /* a half-written or hand-edited entry should not take the page down */
    console.warn('Saved ShopGenie chats are unreadable — starting fresh.');

    return [];
  }
};

const saveChats = (chats) => {
  try {
    localStorage.setItem(historyKey(), JSON.stringify(chats.slice(0, HISTORY_LIMIT)));
  } catch (e) {
    /* quota full, or storage blocked in private mode — chatting still works */
    console.warn('Could not save this chat:', e.message);
  }
};

/* The first thing the customer asked makes a better label than a timestamp */
const titleOf = (messages) => {
  const first = messages.find((m) => m.role === 'user');

  if (!first) {
    return 'New chat';
  }

  const text = first.text.trim();

  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
};

const relativeTime = (iso) => {
  const then = new Date(iso).getTime();

  if (!then) {
    return '';
  }

  const mins = Math.round((Date.now() - then) / 60000);

  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;

  const hours = Math.round(mins / 60);

  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);

  return days === 1 ? 'yesterday' : `${days}d ago`;
};

const newChatId = () => `c${Date.now()}${Math.random().toString(36).slice(2, 7)}`;

function AssistantPage() {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(newChatId);
  const [historyOpen, setHistoryOpen] = useState(false);

  /*
    Where to draw the history panel.

    Both .assistant-hero and .assistant-wrap use overflow:hidden — the hero
    for its drifting blobs, the wrap to stop the page scrolling — so a panel
    positioned inside either is clipped at their edges. It is rendered into
    document.body instead and pinned to the button's real screen position.
  */
  const [panelPos, setPanelPos] = useState(null);

  const bodyRef = useRef(null);
  const historyRef = useRef(null);
  const panelRef = useRef(null);
  const historyBtnRef = useRef(null);

  /*
    The assistant fills the viewport below the navbar. The navbar is
    sticky="top" so it takes real flow space, and its height changes with the
    breakpoint — measuring it beats hard-coding a number that would leave a
    gap or a scrollbar on some screens.
  */
  const [navHeight, setNavHeight] = useState(72);

  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('nav.navbar') || document.querySelector('nav');
      if (nav) {
        setNavHeight(nav.getBoundingClientRect().height);
      }
    };

    measure();

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    setChats(loadChats());
  }, []);

  /*
    Save after every exchange rather than on a "save" button, so closing the
    tab mid-conversation loses nothing. A chat with no question in it yet is
    not worth an entry, so the welcome-only state is skipped.
  */
  useEffect(() => {
    if (!messages.some((m) => m.role === 'user')) {
      return;
    }

    setChats((prev) => {
      const entry = {
        id: chatId,
        title: titleOf(messages),
        at: new Date().toISOString(),
        messages
      };

      const next = [entry, ...prev.filter((c) => c.id !== chatId)];

      saveChats(next);

      return next.slice(0, HISTORY_LIMIT);
    });
  }, [messages, chatId]);

  /* Pin the panel under the button, kept inside the viewport on narrow screens */
  const placePanel = useCallback(() => {
    const btn = historyBtnRef.current;

    if (!btn) {
      return;
    }

    const r = btn.getBoundingClientRect();

    const width = Math.min(330, window.innerWidth - 24);

    /* right-align to the button, but never let the panel run off the left */
    const right = Math.min(
      window.innerWidth - r.right,
      window.innerWidth - width - 12
    );

    setPanelPos({
      top: r.bottom + 10,
      right: Math.max(12, right),
      width
    });
  }, []);

  /* Click anywhere else, or press Escape, to dismiss the history panel */
  useEffect(() => {
    if (!historyOpen) {
      return undefined;
    }

    placePanel();

    const onPointer = (e) => {
      const inButtons = historyRef.current && historyRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);

      /* the panel lives outside the button's subtree now, so both must miss */
      if (!inButtons && !inPanel) {
        setHistoryOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setHistoryOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [historyOpen, placePanel]);

  const startNewChat = () => {
    /* the running conversation is already saved by the effect above, so this
       only has to clear the board */
    setMessages([WELCOME]);
    setInput('');
    setChatId(newChatId());
    setHistoryOpen(false);
  };

  const openChat = (chat) => {
    setMessages(chat.messages);
    setChatId(chat.id);
    setInput('');
    setHistoryOpen(false);
  };

  const deleteChat = (e, id) => {
    /* the row itself opens the chat — don't do both */
    e.stopPropagation();

    setChats((prev) => {
      const next = prev.filter((c) => c.id !== id);

      saveChats(next);

      return next;
    });

    /* deleting the chat you are reading leaves you on a fresh one */
    if (id === chatId) {
      setMessages([WELCOME]);
      setChatId(newChatId());
    }
  };

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();

    if (!text || loading) {
      return;
    }

    const userMsg = { role: 'user', text };
    const nextMessages = [...messages, userMsg];

    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const history = nextMessages
        .filter((m, i) => !(i === 0 && m === WELCOME))
        .map((m) => ({ role: m.role, text: m.text }));

      const { data } = await axios.post(`${API}/ai/chat`, {
        message: text,
        history,
        mode: 'shopping'
      });

      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: data.reply,
          products: data.products || [],
          comparison: data.comparison || null
        }
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Sorry, I could not reach the assistant. Please try again.',
          products: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <CustomNavbar />

      <div
        className="assistant-wrap"
        style={{ '--nav-h': `${navHeight}px` }}
      >
        {/* Header bar — full width, the page's own top edge */}
        <div className="assistant-hero">
          {/* Slow-drifting colour blobs behind the gradient */}
          <span className="assistant-hero-blob assistant-hero-blob-1" />
          <span className="assistant-hero-blob assistant-hero-blob-2" />
          <span className="assistant-hero-shine" />

          <div className="assistant-hero-inner">
            <div className="assistant-hero-icon">
              <span className="assistant-hero-ring" />
              <span className="assistant-hero-face">🧞</span>
            </div>
            <div className="assistant-hero-copy">
              <h1 className="fw-bold mb-1">ShopGenie</h1>
              <p className="mb-0 assistant-hero-text">
                Compare products and get personalized suggestions
              </p>
            </div>

            <div className="assistant-hero-actions" ref={historyRef}>
              <button
                type="button"
                className="assistant-act"
                onClick={startNewChat}
                title="Start a fresh conversation"
              >
                <span className="assistant-act-ico">✨</span>
                <span className="assistant-act-label">New chat</span>
              </button>

              <button
                type="button"
                ref={historyBtnRef}
                className={`assistant-act ${historyOpen ? 'is-open' : ''}`}
                onClick={() => setHistoryOpen((v) => !v)}
                title="Your earlier conversations"
              >
                <span className="assistant-act-ico">🕘</span>
                <span className="assistant-act-label">History</span>
                {chats.length > 0 && (
                  <span className="assistant-act-count">{chats.length}</span>
                )}
              </button>

              {historyOpen && panelPos && createPortal(
                <div
                  className="assistant-history"
                  ref={panelRef}
                  style={{
                    top: `${panelPos.top}px`,
                    right: `${panelPos.right}px`,
                    width: `${panelPos.width}px`
                  }}
                >
                  <div className="assistant-history-head">
                    <span>Recent chats</span>
                    {chats.length > 0 && (
                      <button
                        type="button"
                        className="assistant-history-clear"
                        onClick={() => {
                          setChats([]);
                          saveChats([]);
                          startNewChat();
                        }}
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {chats.length === 0 ? (
                    <p className="assistant-history-empty">
                      No saved chats yet. Ask something and it will appear here.
                    </p>
                  ) : (
                    <ul className="assistant-history-list">
                      {chats.map((c) => (
                        <li
                          key={c.id}
                          className={`assistant-history-item ${
                            c.id === chatId ? 'is-active' : ''
                          }`}
                          onClick={() => openChat(c)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') openChat(c);
                          }}
                        >
                          <span className="assistant-history-title">
                            {c.title}
                          </span>
                          <span className="assistant-history-meta">
                            {relativeTime(c.at)}
                          </span>
                          <button
                            type="button"
                            className="assistant-history-del"
                            title="Delete this chat"
                            onClick={(e) => deleteChat(e, c.id)}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>,
                document.body
              )}
            </div>
          </div>
        </div>

        {/* Conversation fills whatever height is left */}
        <div className="assistant-card">
          <div className="assistant-body" ref={bodyRef}>
            <div className="assistant-stream">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`assistant-msg ${m.role === 'user' ? 'user' : 'bot'}`}
                >
                  <div className="assistant-line">
                    {m.role !== 'user' && (
                      <span className="assistant-av">🧞</span>
                    )}
                    <div className="assistant-bubble">{m.text}</div>
                  </div>

                  {m.comparison && m.comparison.length > 0 && (
                    <div className="assistant-compare">
                      <div className="assistant-compare-scroll">
                        <table className="assistant-compare-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Category</th>
                              <th>Description</th>
                              <th>Highlights</th>
                              <th>Rating</th>
                              <th>Availability</th>
                              <th>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.comparison.map((c, ci) => (
                              <tr key={`${c._id}-${ci}`}>
                                <td>
                                  <Link
                                    to={`/product/${c._id}`}
                                    className="assistant-compare-prod"
                                  >
                                    <img src={c.image} alt={c.name} />
                                    <span>{c.name}</span>
                                  </Link>
                                </td>
                                <td className="assistant-compare-cat">
                                  {c.category || '—'}
                                </td>
                                <td className="assistant-compare-desc">
                                  {c.description || '—'}
                                </td>
                                <td className="assistant-compare-hl">
                                  {c.highlights && c.highlights.length > 0 ? (
                                    <ul>
                                      {c.highlights.slice(0, 4).map((h, hi) => (
                                        <li key={hi}>{h}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="assistant-compare-rating">
                                  {c.rating
                                    ? `⭐ ${c.rating} (${c.ratingCount})`
                                    : 'No ratings'}
                                </td>
                                <td className="assistant-compare-stock">
                                  {c.stock > 0 ? (
                                    <span className="in-stock">
                                      In stock ({c.stock})
                                    </span>
                                  ) : (
                                    <span className="out-stock">
                                      Out of stock
                                    </span>
                                  )}
                                </td>
                                <td className="assistant-compare-price">
                                  ₹{c.price}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {m.products && m.products.length > 0 && (
                    <div className="assistant-results">
                      {m.products.map((p, pi) => (
                        <Link
                          key={`${p._id}-${pi}`}
                          to={`/product/${p._id}`}
                          className={`assistant-result ${
                            p.rank === 1 ? 'is-top' : ''
                          }`}
                          style={{ '--i': pi }}
                        >
                          {p.badge && (
                            <span className="assistant-result-badge">
                              {p.badge}
                            </span>
                          )}

                          <span className="assistant-result-media">
                            <img src={p.image} alt={p.name} />
                            {p.rank && (
                              <span className="assistant-result-rank">
                                {p.rank}
                              </span>
                            )}
                          </span>

                          <span className="assistant-result-main">
                            <span className="assistant-result-name">
                              {p.name}
                            </span>

                            {p.blurb && (
                              <span className="assistant-result-blurb">
                                {p.blurb}
                              </span>
                            )}

                            <span className="assistant-result-meta">
                              {p.rating != null ? (
                                <span className="assistant-tag is-rating">
                                  ★ {p.rating}
                                  <small>({p.ratingCount})</small>
                                </span>
                              ) : (
                                <span className="assistant-tag is-muted">
                                  No ratings yet
                                </span>
                              )}

                              {p.stock > 0 ? (
                                <span
                                  className={`assistant-tag ${
                                    p.stock <= 5 ? 'is-low' : 'is-stock'
                                  }`}
                                >
                                  {p.stock <= 5
                                    ? `Only ${p.stock} left`
                                    : 'In stock'}
                                </span>
                              ) : (
                                <span className="assistant-tag is-out">
                                  Out of stock
                                </span>
                              )}
                            </span>
                          </span>

                          <span className="assistant-result-price">
                            ₹{Number(p.price).toLocaleString('en-IN')}
                            <span className="assistant-result-cta">View →</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}

                </div>
              ))}

              {loading && (
                <div className="assistant-msg bot">
                  <div className="assistant-line">
                    <span className="assistant-av">🧞</span>
                    <div className="assistant-bubble assistant-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="assistant-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="assistant-chip"
                      onClick={() => sendMessage(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="assistant-input">
            <div className="assistant-input-inner">
              <textarea
                rows={1}
                placeholder="Ask me to compare or suggest products..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
              >
                Send ➤
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{assistantStyles}</style>
    </>
  );
}

const assistantStyles = `
  /*
    Full-page chat: the wrap owns exactly the viewport below the sticky
    navbar and lays out header / stream / composer as a flex column. The
    stream is the only thing that scrolls, so the composer stays put and the
    page itself never scrolls.
    --nav-h is measured from the real navbar in the component.
  */
  .assistant-wrap {
    background:
      radial-gradient(1100px 380px at 100% -10%, rgba(234, 88, 12,0.16), transparent),
      radial-gradient(900px 380px at -10% 8%, rgba(124,58,237,0.14), transparent),
      linear-gradient(180deg, #faf5ff 0%, #fff7ed 60%, #fef3c7 100%);
    height: calc(100vh - var(--nav-h, 72px));
    height: calc(100dvh - var(--nav-h, 72px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    /* breathing room so the header does not sit flush against the navbar */
    padding-top: clamp(10px, 1.6vw, 18px);
  }

  /* ===================== Hero ===================== */
  /* Header sits just inside the page edges, with a gap above it */
  .assistant-hero {
    flex-shrink: 0;
    background: linear-gradient(120deg, #0f172a, #4c1d95 35%, #7c2d12 70%, #ea580c);
    background-size: 220% 220%;
    color: #fff;
    padding: 14px clamp(18px, 3vw, 30px);
    margin: 0 clamp(12px, 2.5vw, 26px);
    border-radius: 18px;
    box-shadow: 0 12px 30px rgba(49, 46, 129, 0.3);
    position: relative;
    overflow: hidden;
    z-index: 2;
    /* the gradient drifts, so the banner is never quite still */
    animation: assistantHeroShift 14s ease-in-out infinite,
               assistantHeroIn 0.6s cubic-bezier(0.22, 1.2, 0.36, 1);
  }
  @keyframes assistantHeroShift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  @keyframes assistantHeroIn {
    from { opacity: 0; transform: translateY(-14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .assistant-hero::after {
    content: '';
    position: absolute;
    top: -60px; right: -30px;
    width: 220px; height: 220px;
    background: radial-gradient(circle, rgba(245, 158, 11,0.5), transparent 70%);
    pointer-events: none;
  }
  .assistant-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(46px);
    pointer-events: none;
  }
  .assistant-hero-blob-1 {
    width: 190px; height: 190px;
    background: #a855f7;
    top: -110px; left: 18%;
    opacity: 0.55;
    animation: assistantBlob1 11s ease-in-out infinite;
  }
  .assistant-hero-blob-2 {
    width: 160px; height: 160px;
    background: #f59e0b;
    bottom: -110px; left: 46%;
    opacity: 0.45;
    animation: assistantBlob2 13s ease-in-out infinite;
  }
  @keyframes assistantBlob1 {
    0%, 100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(44px, 22px) scale(1.2); }
  }
  @keyframes assistantBlob2 {
    0%, 100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(-38px, -18px) scale(1.25); }
  }
  /* Light sweeping across the banner */
  .assistant-hero-shine {
    position: absolute;
    top: 0;
    left: -140%;
    width: 45%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.16), transparent);
    transform: skewX(-20deg);
    pointer-events: none;
    animation: assistantHeroSheen 7s ease-in-out infinite;
  }
  @keyframes assistantHeroSheen {
    0%, 55% { left: -140%; }
    85%     { left: 170%; }
    100%    { left: 170%; }
  }

  .assistant-hero-icon {
    width: 58px;
    height: 58px;
    display: grid;
    place-items: center;
    background:
      radial-gradient(circle at 50% 38%, rgba(253,230,138,0.6), transparent 62%),
      linear-gradient(135deg, #6d28d9, #ea580c);
    border: 2px solid rgba(253,230,138,0.7);
    border-radius: 50%;
    font-size: 30px;
    box-shadow: 0 0 0 6px rgba(251,191,36,0.15), 0 0 20px rgba(245,158,11,0.5);
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }
  /* Rotating gold halo around the genie */
  .assistant-hero-ring {
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    background: conic-gradient(from 0deg,
      transparent, rgba(253,224,71,0.95), transparent 45%,
      transparent, rgba(167,139,250,0.9), transparent 95%);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
    animation: assistantSpin 5s linear infinite;
    pointer-events: none;
  }
  @keyframes assistantSpin {
    to { transform: rotate(360deg); }
  }
  /* positioned so it paints above the halo ring, not under it */
  .assistant-hero-face {
    position: relative;
    z-index: 1;
    display: block;
    line-height: 1;
    animation: assistantGenieBob 5.5s ease-in-out infinite;
  }
  @keyframes assistantGenieBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25%      { transform: translateY(-3px) rotate(-8deg); }
    60%      { transform: translateY(2px) rotate(7deg); }
  }

  /* Content of the full-width bar stays aligned with the message column */
  .assistant-hero-inner {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
  }

  /* ============ New chat / History ============ */
  /*
    The controls sit on the dark hero, so they are glass rather than solid —
    a white panel here would fight the gradient behind it. margin-left:auto
    pushes them to the far edge without a spacer element.
  */
  .assistant-hero-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
  }

  .assistant-act {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.13);
    color: #fff;
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    backdrop-filter: blur(6px);
    transition: background 0.18s ease, border-color 0.18s ease,
                transform 0.18s ease;
  }
  .assistant-act:hover {
    background: rgba(255, 255, 255, 0.24);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
  }
  .assistant-act:active { transform: translateY(0); }
  .assistant-act.is-open {
    background: rgba(255, 255, 255, 0.92);
    border-color: #fff;
    color: #4c1d95;
  }
  .assistant-act-ico { font-size: 0.95rem; line-height: 1; }

  /* a small tally so the button says whether history is worth opening */
  .assistant-act-count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(251, 191, 36, 0.95);
    color: #442c00;
    font-size: 0.7rem;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  /* ---- the dropdown ---- */
  /*
    Rendered into document.body, not into the hero — two ancestors use
    overflow:hidden and would clip it. top/right/width are set inline from the
    button's measured position, so nothing here may override them.
  */
  .assistant-history {
    position: fixed;
    background: #fffdf9;
    border: 1px solid rgba(124, 58, 237, 0.18);
    border-radius: 16px;
    box-shadow: 0 18px 44px rgba(49, 46, 129, 0.28);
    overflow: hidden;
    /* body-level now, so it must clear the sticky navbar (1050) and the
       floating chat launcher (9999) rather than the hero's local stack */
    z-index: 10000;
    animation: assistantHistoryIn 0.18s ease-out;
  }
  @keyframes assistantHistoryIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .assistant-history-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    background: linear-gradient(120deg, #4c1d95, #7c2d12);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .assistant-history-clear {
    border: none;
    background: none;
    color: #fde68a;
    font-size: 0.74rem;
    font-weight: 700;
    cursor: pointer;
    text-transform: none;
    letter-spacing: 0;
  }
  .assistant-history-clear:hover { color: #fff; text-decoration: underline; }

  .assistant-history-empty {
    margin: 0;
    padding: 18px 16px;
    font-size: 0.85rem;
    color: #78716c;
    line-height: 1.5;
  }

  .assistant-history-list {
    list-style: none;
    margin: 0;
    padding: 6px;
    max-height: 320px;
    overflow-y: auto;
  }

  .assistant-history-item {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .assistant-history-item:hover { background: #f5f3ff; }
  .assistant-history-item.is-active {
    background: #ede9fe;
    box-shadow: inset 3px 0 0 #7c3aed;
  }

  .assistant-history-title {
    font-size: 0.86rem;
    font-weight: 600;
    color: #1c1917;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .assistant-history-meta {
    font-size: 0.72rem;
    color: #a8a29e;
    white-space: nowrap;
  }
  .assistant-history-del {
    border: none;
    background: none;
    color: #d6d3d1;
    font-size: 0.8rem;
    line-height: 1;
    padding: 3px 5px;
    border-radius: 6px;
    cursor: pointer;
  }
  .assistant-history-del:hover { background: #fee2e2; color: #dc2626; }

  @media (prefers-reduced-motion: reduce) {
    .assistant-act,
    .assistant-history { transition: none; animation: none; }
    .assistant-act:hover { transform: none; }
  }
  .assistant-hero-copy {
    position: relative;
    z-index: 1;
  }
  .assistant-hero h1 {
    font-size: clamp(1.5rem, 4vw, 2.1rem);
    background: linear-gradient(100deg, #fff, #fde68a 40%, #fff 70%, #fde68a);
    background-size: 220% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: assistantTitleShine 6s linear infinite;
  }
  @keyframes assistantTitleShine {
    to { background-position: 220% center; }
  }
  .assistant-hero-text {
    opacity: 0.85;
  }

  /* No card chrome any more — this is just the flexing middle of the page */
  .assistant-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    animation: assistantCardIn 0.5s cubic-bezier(0.22, 1.2, 0.36, 1) 0.1s backwards;
  }
  @keyframes assistantCardIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* The only scrolling region on the page */
  .assistant-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: clamp(16px, 3vw, 28px) clamp(16px, 4vw, 34px);
  }
  /* Messages keep a readable measure even on a very wide monitor, while the
     background and the composer stay full-bleed. */
  .assistant-stream {
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
  }
  .assistant-body::-webkit-scrollbar { width: 8px; }
  .assistant-body::-webkit-scrollbar-thumb {
    background: linear-gradient(#c4b5fd, #fcd34d);
    border-radius: 8px;
  }
  .assistant-body::-webkit-scrollbar-track { background: transparent; }

  .assistant-msg {
    margin-bottom: 18px;
    display: flex;
    flex-direction: column;
  }
  .assistant-msg.user { align-items: flex-end; }
  .assistant-msg.bot { align-items: flex-start; }

  .assistant-line {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    max-width: 90%;
  }
  .assistant-msg.user .assistant-line { flex-direction: row-reverse; }

  .assistant-av {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background:
      radial-gradient(circle at 50% 38%, rgba(253,230,138,0.5), transparent 62%),
      linear-gradient(135deg, #6d28d9, #ea580c);
    font-size: 18px;
    box-shadow: 0 4px 10px rgba(124,58,237,0.4);
    /* pops in just after its bubble, so the genie "arrives" with the reply */
    animation: assistantAvIn 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 0.06s backwards;
  }
  @keyframes assistantAvIn {
    from { opacity: 0; transform: scale(0.3) rotate(-40deg); }
    to   { opacity: 1; transform: scale(1) rotate(0); }
  }

  .assistant-bubble {
    padding: 12px 16px;
    border-radius: 18px;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    box-shadow: 0 4px 14px rgba(15,23,42,0.06);
    /* each message springs in from its own side of the conversation */
    animation: assistantBubbleIn 0.45s cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
  }
  @keyframes assistantBubbleIn {
    from { opacity: 0; transform: translate(-18px, 10px) scale(0.94); }
    to   { opacity: 1; transform: translate(0, 0) scale(1); }
  }
  @keyframes assistantBubbleInRight {
    from { opacity: 0; transform: translate(18px, 10px) scale(0.94); }
    to   { opacity: 1; transform: translate(0, 0) scale(1); }
  }
  .assistant-msg.user .assistant-bubble {
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    border-bottom-right-radius: 5px;
    box-shadow: 0 8px 20px rgba(234, 88, 12, 0.3);
    animation-name: assistantBubbleInRight;
  }
  .assistant-msg.bot .assistant-bubble {
    background: #fff;
    color: #1e293b;
    border: 1px solid #ece3f7;
    border-bottom-left-radius: 5px;
    box-shadow: 0 8px 20px rgba(109, 40, 217, 0.08);
  }

  /* ===================== Result rows =====================
     The search results used to be a numbered list inside the reply text.
     They are rows now: rank, thumbnail, name, rating and stock each get
     their own slot, so the eye can scan down a column instead of reading
     four near-identical sentences. */
  .assistant-results {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
    width: min(100%, 620px);
  }
  .assistant-result {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #ece3f7;
    border-radius: 16px;
    text-decoration: none;
    color: inherit;
    overflow: hidden;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.22s ease, border-color 0.22s ease;
    /* --i is the row index, so the list cascades however long it is */
    animation: assistantRowPop 0.45s cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 0.08s + 0.1s);
  }
  @keyframes assistantRowPop {
    from { opacity: 0; transform: translateX(-18px) scale(0.97); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
  /* A light sweeps across on hover */
  .assistant-result::after {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(167,139,250,0.16), transparent);
    transform: skewX(-20deg);
    opacity: 0;
    transition: left 0.55s ease, opacity 0.2s ease;
    pointer-events: none;
  }
  .assistant-result:hover::after { left: 130%; opacity: 1; }
  .assistant-result:hover {
    transform: translateY(-3px);
    border-color: #c4b5fd;
    box-shadow: 0 16px 30px rgba(109, 40, 217, 0.18);
  }
  /* The top result earns a warmer frame */
  .assistant-result.is-top {
    border-color: #fcd34d;
    background: linear-gradient(135deg, #fffdf7, #fff);
    box-shadow: 0 10px 24px rgba(245, 158, 11, 0.16);
  }
  .assistant-result.is-top:hover {
    box-shadow: 0 18px 34px rgba(245, 158, 11, 0.3);
  }

  .assistant-result-badge {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #7c2d12;
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    padding: 3px 12px;
    border-bottom-left-radius: 10px;
    box-shadow: 0 3px 8px rgba(180, 83, 9, 0.25);
  }

  .assistant-result-media {
    position: relative;
    flex-shrink: 0;
  }
  .assistant-result-media img {
    width: 62px;
    height: 62px;
    object-fit: cover;
    border-radius: 12px;
    display: block;
    background: #faf5ff;
    transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .assistant-result:hover .assistant-result-media img {
    transform: scale(1.08) rotate(-2deg);
  }
  .assistant-result-rank {
    position: absolute;
    top: -6px;
    left: -6px;
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    font-size: 0.7rem;
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #6d28d9, #ea580c);
    border: 2px solid #fff;
    box-shadow: 0 3px 8px rgba(109, 40, 217, 0.35);
  }
  .assistant-result.is-top .assistant-result-rank {
    background: linear-gradient(135deg, #f59e0b, #b45309);
  }

  .assistant-result-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .assistant-result-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }
  .assistant-result:hover .assistant-result-name { color: #6d28d9; }
  .assistant-result-blurb {
    font-size: 0.8rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .assistant-result-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 3px;
  }
  .assistant-tag {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 9px;
    border-radius: 20px;
    white-space: nowrap;
  }
  .assistant-tag small { font-weight: 600; opacity: 0.75; margin-left: 2px; }
  .assistant-tag.is-rating { background: #fef3c7; color: #b45309; }
  .assistant-tag.is-stock  { background: #dcfce7; color: #15803d; }
  .assistant-tag.is-low    { background: #ffedd5; color: #c2410c; }
  .assistant-tag.is-out    { background: #fee2e2; color: #b91c1c; }
  .assistant-tag.is-muted  { background: #f1f5f9; color: #94a3b8; }

  .assistant-result-price {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    font-size: 1.05rem;
    font-weight: 800;
    color: #16a34a;
  }
  .assistant-result-cta {
    font-size: 0.68rem;
    font-weight: 700;
    color: #6d28d9;
    background: #f5f3ff;
    padding: 3px 10px;
    border-radius: 20px;
    opacity: 0;
    transform: translateX(-4px);
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .assistant-result:hover .assistant-result-cta {
    opacity: 1;
    transform: translateX(0);
  }

  .assistant-compare {
    margin-top: 12px;
    width: 100%;
    max-width: 100%;
  }
  .assistant-compare-scroll {
    overflow-x: auto;
    border: 1px solid #ece3f7;
    border-radius: 14px;
    box-shadow: 0 10px 28px rgba(109, 40, 217, 0.1);
    /* the table unrolls once the reply has landed */
    animation: assistantTableIn 0.5s cubic-bezier(0.22, 1.2, 0.36, 1) 0.1s backwards;
  }
  @keyframes assistantTableIn {
    from { opacity: 0; transform: translateY(14px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .assistant-compare-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    font-size: 0.88rem;
  }
  .assistant-compare-table thead th {
    background: linear-gradient(180deg, #faf5ff, #f6f0ff);
    text-align: left;
    padding: 10px 14px;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6d28d9;
    border-bottom: 2px solid #e9ddfb;
    position: sticky;
    top: 0;
  }
  .assistant-compare-table td {
    padding: 12px 14px;
    border-bottom: 1px solid #f4eefc;
    vertical-align: middle;
  }
  .assistant-compare-table tr:last-child td {
    border-bottom: none;
  }
  /* rows fade in one after another, so the comparison reads top-to-bottom */
  .assistant-compare-table tbody tr {
    transition: background 0.2s ease;
    animation: assistantRowIn 0.4s ease backwards;
  }
  .assistant-compare-table tbody tr:nth-child(1) { animation-delay: 0.16s; }
  .assistant-compare-table tbody tr:nth-child(2) { animation-delay: 0.26s; }
  .assistant-compare-table tbody tr:nth-child(3) { animation-delay: 0.36s; }
  .assistant-compare-table tbody tr:nth-child(n+4) { animation-delay: 0.46s; }
  @keyframes assistantRowIn {
    from { opacity: 0; transform: translateX(-14px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .assistant-compare-table tbody tr:hover {
    background: #faf5ff;
  }
  .assistant-compare-prod {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    color: #1e293b;
    font-weight: 600;
    min-width: 150px;
  }
  .assistant-compare-prod img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 8px;
    flex-shrink: 0;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.25s ease;
  }
  .assistant-compare-prod:hover img {
    transform: scale(1.12) rotate(-3deg);
    box-shadow: 0 8px 18px rgba(109, 40, 217, 0.3);
  }
  .assistant-compare-prod span {
    transition: color 0.2s ease;
  }
  .assistant-compare-prod:hover span {
    color: #ea580c;
  }
  .assistant-compare-cat {
    white-space: nowrap;
    color: #475569;
  }
  .assistant-compare-desc {
    color: #475569;
    min-width: 170px;
    max-width: 240px;
  }
  .assistant-compare-hl {
    min-width: 170px;
    max-width: 230px;
    color: #475569;
  }
  .assistant-compare-hl ul {
    margin: 0;
    padding-left: 16px;
  }
  .assistant-compare-hl li {
    margin-bottom: 2px;
  }
  .assistant-compare-rating {
    white-space: nowrap;
    font-weight: 600;
    color: #b45309;
  }
  .assistant-compare-stock {
    white-space: nowrap;
    font-weight: 600;
  }
  .assistant-compare-stock .in-stock {
    color: #16a34a;
  }
  .assistant-compare-stock .out-stock {
    color: #dc2626;
  }
  .assistant-compare-price {
    white-space: nowrap;
    font-weight: 800;
    color: #16a34a;
  }

  .assistant-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .assistant-chip {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #b45309;
    border: 1px solid #fcd34d;
    border-radius: 22px;
    padding: 9px 16px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.22s ease, color 0.2s ease;
    animation: assistantChipIn 0.5s cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
  }
  .assistant-chip:nth-child(1) { animation-delay: 0.12s; }
  .assistant-chip:nth-child(2) { animation-delay: 0.22s; }
  .assistant-chip:nth-child(3) { animation-delay: 0.32s; }
  .assistant-chip:nth-child(4) { animation-delay: 0.42s; }
  @keyframes assistantChipIn {
    from { opacity: 0; transform: translateY(12px) scale(0.9); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .assistant-chip:hover {
    transform: translateY(-3px) scale(1.04);
    color: #7c2d12;
    box-shadow: 0 10px 20px rgba(245, 158, 11, 0.38);
  }

  .assistant-typing {
    display: inline-flex;
    gap: 5px;
    align-items: center;
  }
  .assistant-typing span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6d28d9, #ea580c);
    /* bouncing reads as "thinking" far better than fading */
    animation: assistantBounce 1.1s infinite ease-in-out;
  }
  .assistant-typing span:nth-child(2) { animation-delay: 0.15s; }
  .assistant-typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes assistantBounce {
    0%, 70%, 100% { transform: translateY(0) scale(1);       opacity: 0.4; }
    35%           { transform: translateY(-7px) scale(1.15); opacity: 1; }
  }

  /*
    The composer is a centred pill rather than a full-width bar. A bar would
    run underneath the floating corner buttons — BackgroundMusic sits at
    left:14px and the Help bubble at right:22px — so keeping it narrow and
    centred leaves both of them clearly visible and clickable.
  */
  .assistant-input {
    flex-shrink: 0;
    padding: 10px clamp(90px, 12vw, 150px) 16px;
    background: transparent;
  }
  .assistant-input-inner {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    padding: 7px 7px 7px 14px;
    background: rgba(255,255,255,0.96);
    backdrop-filter: blur(8px);
    border: 1px solid #e9ddfb;
    border-radius: 26px;
    box-shadow: 0 10px 30px rgba(109, 40, 217, 0.14);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .assistant-input-inner:focus-within {
    border-color: #c4b5fd;
    box-shadow: 0 14px 36px rgba(109, 40, 217, 0.22);
  }
  /* The pill itself carries the border and focus ring now, so the textarea
     is bare — otherwise there are two nested boxes. */
  .assistant-input textarea {
    flex: 1;
    resize: none;
    border: none;
    background: transparent;
    border-radius: 0;
    padding: 7px 2px;
    font-size: 0.88rem;
    line-height: 1.45;
    max-height: 88px;
    outline: none;
  }
  .assistant-input button {
    position: relative;
    overflow: hidden;
    border: none;
    background: linear-gradient(135deg, #6d28d9, #ea580c 60%, #f59e0b);
    background-size: 190% 190%;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 9px 18px;
    border-radius: 20px;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: 0 6px 14px rgba(234, 88, 12,0.32);
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.22s ease, background-position 0.4s ease;
  }
  /* shine sweeping across the send button while it is usable */
  .assistant-input button:not(:disabled)::after {
    content: '';
    position: absolute;
    top: 0;
    left: -130%;
    width: 45%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent);
    transform: skewX(-20deg);
    animation: assistantSendSheen 4s ease-in-out infinite;
  }
  @keyframes assistantSendSheen {
    0%, 60% { left: -130%; }
    88%     { left: 170%; }
    100%    { left: 170%; }
  }
  .assistant-input button:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.04);
    background-position: right center;
    box-shadow: 0 14px 28px rgba(124, 58, 237, 0.45);
  }
  .assistant-input button:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
  }
  .assistant-input button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }

  /* All of the above is decoration — with motion reduced it stops, and the
     assistant behaves identically. */
  @media (prefers-reduced-motion: reduce) {
    .assistant-hero,
    .assistant-hero-blob,
    .assistant-hero-shine,
    .assistant-hero-ring,
    .assistant-hero-face,
    .assistant-hero h1,
    .assistant-card,
    .assistant-av,
    .assistant-bubble,
    .assistant-result,
    .assistant-compare-scroll,
    .assistant-compare-table tbody tr,
    .assistant-chip,
    .assistant-input button:not(:disabled)::after {
      animation: none !important;
    }
    .assistant-hero-blob, .assistant-hero-shine { display: none; }
    .assistant-hero h1 {
      -webkit-text-fill-color: #fff;
      background: none;
    }
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 768px) {
    .assistant-hero {
      padding: 12px 16px;
    }
    .assistant-hero-inner { gap: 12px; }
    .assistant-hero-icon {
      width: 46px;
      height: 46px;
      font-size: 24px;
    }
    /* the strapline is the first thing to go when height is tight */
    .assistant-hero-text { display: none; }
    /* the icons still say which button is which, so the words can go */
    .assistant-act-label { display: none; }
    .assistant-act { padding: 8px 11px; }
    .assistant-line { max-width: 100%; }
    .assistant-bubble { font-size: 0.9rem; }
    .assistant-results { width: 100%; }
    .assistant-result { gap: 10px; padding: 10px 12px; }
    .assistant-result-media img { width: 52px; height: 52px; }
    .assistant-result-price { font-size: 0.95rem; }
    .assistant-av {
      width: 28px;
      height: 28px;
      font-size: 15px;
    }
    .assistant-input button {
      padding: 9px 14px;
    }
  }

  /* Below 1200px the corner launchers hide in favour of the mobile speed
     dial, so the composer no longer needs to keep clear of them — but
     MobileBottomNav becomes fixed over the bottom 66px, so the page has to
     give that height back or the composer ends up underneath it. */
  @media (max-width: 1199.98px) {
    .assistant-wrap {
      height: calc(100vh - var(--nav-h, 72px) - 66px - env(safe-area-inset-bottom, 0px));
      height: calc(100dvh - var(--nav-h, 72px) - 66px - env(safe-area-inset-bottom, 0px));
    }
    .assistant-input {
      padding: 10px clamp(14px, 4vw, 28px) 12px;
    }
  }

  @media (max-width: 480px) {
    /* the blurb is the first thing to go when the row gets tight */
    .assistant-result-blurb { display: none; }
    .assistant-result-cta { display: none; }
    .assistant-input button {
      padding: 9px 13px;
      font-size: 0;
    }
    .assistant-input button::before {
      content: '➤';
      font-size: 1rem;
    }
  }
`;

export default AssistantPage;
